/**
 * A tour sheet's links to the SHARED pressure-control records (BOP-2): the
 * pressure tests and crew drills done during the sheet (fieldiq-api#1286).
 *
 * The records belong to /pressure-control; the sheet only points at them, read
 * through BOP-2's readTest / readDrill. Links change only while the sheet is a
 * draft (the tour roles). Managing opens the API's candidate list — records
 * inside the sheet's window — and a record already on ANOTHER sheet is shown
 * but cannot be ticked (one sheet per record). Saving sends the whole ticked
 * set (PUT), only for the list that changed.
 */
import React, { useMemo, useState } from 'react';
import { Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { useSetWsTourLinks, useWsLinkCandidates } from '../../hooks/useWsString';
import { formatInstant } from '../../lib/calendar-date';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { pcDrillKindLabel, pcReviewSkin, pcTestKindLabel, PC_RESULT_LABEL, readDrill, readTest } from '../../lib/pressure-control';
import { fmtWsNumber, wsJobRefusal, wsLinkSelectable, wsLinkSet } from '../../lib/well-servicing';
import type { PcDrill, PcTest } from '../../types/pressure-control';
import type { WsTour } from '../../types/well-servicing';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { WS_PRIMARY, WS_SECONDARY, WsEmpty, WsFormError, WsLoading, WsPanel } from './WsBits';

export const testLine = (t: PcTest, tz: string | null) =>
  `${pcTestKindLabel(t.testKind)} · ${formatInstant(t.testedAt, tz) ?? '—'} · ${t.stackName || '—'} · ${t.result ? PC_RESULT_LABEL[t.result] ?? t.result : '—'}`;
export const drillLine = (d: PcDrill, tz: string | null) =>
  `${pcDrillKindLabel(d.drillKind)} · ${formatInstant(d.drilledAt, tz) ?? '—'} · ${d.rigName || '—'} · ${
    d.secondsToSecure === null ? '—' : `${fmtWsNumber(d.secondsToSecure)} s to secure`
  }`;

/**
 * Pending / Approved (fieldiq-api#1324). A record a rig manager or driller made
 * on this sheet is pending; approving the SHEET approves it — there is no
 * separate approve here.
 */
function ReviewBadge({ status }: { status: string | null }) {
  const skin = pcReviewSkin(status);
  return (
    <span
      data-testid="ws-link-review"
      className={`inline-flex w-fit items-center whitespace-nowrap rounded-fi-pill border border-black/20 px-2 py-0.5 text-[0.64rem] font-black uppercase tracking-[0.06em] leading-none text-fi-signal-ink ${skin.fill}`}
    >
      {skin.label}
    </span>
  );
}

export function TourLinksPanel({ tour, editable }: { tour: WsTour; editable: boolean }) {
  const tz = tour.timeZone ?? null;
  const tests = (tour.pressureTests ?? []).map((r) => readTest(r as any)).filter((t): t is PcTest => !!t);
  const drills = (tour.drills ?? []).map((r) => readDrill(r as any)).filter((d): d is PcDrill => !!d);
  const [managing, setManaging] = useState(false);
  const candidates = useWsLinkCandidates(tour.id, editable && managing);
  const setLinks = useSetWsTourLinks();
  const [pickedTests, setPickedTests] = useState<Set<string>>(new Set());
  const [pickedDrills, setPickedDrills] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const failure = queryLoadFailure(candidates);
  const cTests = candidates.data?.data.pressureTests ?? [];
  const cDrills = candidates.data?.data.drills ?? [];
  const currentTests = useMemo(() => new Set(tests.map((t) => t.id)), [tests]);
  const currentDrills = useMemo(() => new Set(drills.map((d) => d.id)), [drills]);
  const pendingCount = tests.filter((t) => t.reviewStatus === 'pending').length + drills.filter((d) => d.reviewStatus === 'pending').length;

  const start = () => {
    setError(null);
    setPickedTests(new Set(currentTests));
    setPickedDrills(new Set(currentDrills));
    setManaging(true);
  };
  const toggle = (set: Set<string>, id: string, apply: (s: Set<string>) => void) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    apply(next);
  };
  const same = (a: Set<string>, b: Set<string>) => a.size === b.size && Array.from(a).every((x) => b.has(x));

  const save = async () => {
    setError(null);
    try {
      if (!same(pickedTests, currentTests)) await setLinks.mutateAsync({ tourId: tour.id, kind: 'test', ids: wsLinkSet(pickedTests) });
      if (!same(pickedDrills, currentDrills)) await setLinks.mutateAsync({ tourId: tour.id, kind: 'drill', ids: wsLinkSet(pickedDrills) });
      toast.success('Links saved');
      setManaging(false);
    } catch (err) {
      setError(wsJobRefusal(err, 'The links could not be saved.'));
    }
  };

  const row = (
    c: { id: string; linkedTourId: string | null; onThisTour: boolean },
    label: string,
    picked: Set<string>,
    apply: (s: Set<string>) => void,
    testId: string,
  ) => {
    const selectable = wsLinkSelectable(c);
    return (
      <li key={c.id} className="flex items-start gap-2 text-sm text-fieldiq-text" data-testid={testId}>
        <input
          type="checkbox"
          className="mt-1"
          aria-label={label}
          checked={picked.has(c.id)}
          disabled={!selectable}
          onChange={() => toggle(picked, c.id, apply)}
        />
        <span className={selectable ? '' : 'text-fieldiq-muted'}>
          {label}
          {!selectable && ' · on another tour sheet'}
        </span>
      </li>
    );
  };

  return (
    <WsPanel
      eyebrow="Pressure control"
      title="Pressure tests and drills"
      testId="ws-tour-links"
      actions={
        editable && !managing ? (
          <button type="button" className={WS_PRIMARY} onClick={start} data-testid="ws-manage-links">
            <Link2 className="size-4" aria-hidden="true" />
            Link records
          </button>
        ) : undefined
      }
    >
      <div className="space-y-3 px-4 py-4">
        <WsFormError message={error} />
        {managing ? (
          candidates.isLoading ? (
            <WsLoading label="Loading the records done during this sheet" />
          ) : failure ? (
            <LoadFailureNotice error={queryFailureError(candidates)} kind={failure} subject="the pressure-control records" />
          ) : (
            <>
              <p className="text-sm text-fieldiq-muted">
                Records done during this sheet. A record can be on one tour sheet.
                {pendingCount > 0 && ' Unticking a pending record deletes it: it was recorded on this sheet and never counted.'}
              </p>
              <h3 className="text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Pressure tests</h3>
              {cTests.length === 0 ? (
                <WsEmpty>No pressure test was recorded during this sheet.</WsEmpty>
              ) : (
                <ul className="space-y-1">
                  {cTests.map((c) => {
                    const t = readTest(c as any);
                    return row(c, t ? testLine(t, tz) : c.id, pickedTests, setPickedTests, 'ws-link-candidate-test');
                  })}
                </ul>
              )}
              <h3 className="text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Drills</h3>
              {cDrills.length === 0 ? (
                <WsEmpty>No drill was recorded during this sheet.</WsEmpty>
              ) : (
                <ul className="space-y-1">
                  {cDrills.map((c) => {
                    const d = readDrill(c as any);
                    return row(c, d ? drillLine(d, tz) : c.id, pickedDrills, setPickedDrills, 'ws-link-candidate-drill');
                  })}
                </ul>
              )}
              <div className="flex justify-end gap-2">
                <button type="button" className={WS_SECONDARY} onClick={() => setManaging(false)} disabled={setLinks.isPending}>
                  Cancel
                </button>
                <button type="button" className={WS_PRIMARY} onClick={save} disabled={setLinks.isPending} data-testid="ws-save-links">
                  {setLinks.isPending ? 'Saving…' : 'Save links'}
                </button>
              </div>
            </>
          )
        ) : tests.length === 0 && drills.length === 0 ? (
          <WsEmpty>No pressure test or drill is linked to this sheet.</WsEmpty>
        ) : (
          <>
            {pendingCount > 0 && (
              <p className="text-sm text-fieldiq-muted" data-testid="ws-links-pending-note">
                {pendingCount === 1 ? '1 record was' : `${pendingCount} records were`} recorded on this sheet by the crew and{' '}
                {pendingCount === 1 ? 'is' : 'are'} pending. Approving the sheet approves {pendingCount === 1 ? 'it' : 'them'}.
              </p>
            )}
            <ul className="space-y-1 text-sm text-fieldiq-text" data-testid="ws-linked-records">
              {tests.map((t) => (
                <li key={t.id} className="flex flex-wrap items-center gap-2" data-testid="ws-linked-test">
                  <span>Pressure test · {testLine(t, tz)}</span>
                  <ReviewBadge status={t.reviewStatus} />
                </li>
              ))}
              {drills.map((d) => (
                <li key={d.id} className="flex flex-wrap items-center gap-2" data-testid="ws-linked-drill">
                  <span>Drill · {drillLine(d, tz)}</span>
                  <ReviewBadge status={d.reviewStatus} />
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </WsPanel>
  );
}
